import { Logger } from "./Logger";
import { ApiResource } from "./ApiResource";
import { ApiSearchResource } from "./search";
import { AuviousEventClient } from "./AuviousEventClient";
import { OAuth2Client, } from "./auth";
import { ApiUserResource } from "./user";
/**
 * auvious client implementation for auvious' services.
 *
 * The auvious client instance should be used as a hub for
 * any auvious service client.
 */
export class AuviousCommon {
    /**
     * Constructor.
     *
     * @param args constructor options
     */
    constructor(args) {
        this._useStandardOAuth2 = false;
        this.log = new Logger("AuviousClient");
        this.timers = args.timers;
        // New config, we prefer this.
        this._serverUrl = args.serverUrl;
        this._host = args.serverHost || "/";
        this._port = args.serverPort || 0;
        if (args.useStandardOAuth2 !== undefined) {
            this._useStandardOAuth2 = args.useStandardOAuth2;
        }
        if (args.eventEnabled || args.eventEnabled === undefined) {
            // defaults to true
            this._eventClient = new AuviousEventClient(this, args.assetsLocation, args.mqttUrl, args.eventServerHost || args.serverHost || "localhost", args.eventServerPort || args.serverPort || 8083, args.eventServerPath || "/mqtt", args.eventServerSSLEnabled, args.eventServerUser || args.mqttUsername, args.eventServerPass || args.mqttPassword, args.refreshEventClientConnectionOnUpdatedCredentials);
            if (args.eventHandlers) {
                this._eventClient.connect(args.eventHandlers);
            }
        }
        const authHandlers = {
            // also called on refreshToken
            onAuthorized: (ev) => {
                this.log.debug("onAuthorized(): ", ev);
                if (this._eventClient && args.eventServerJwtOn) {
                    this._eventClient.updateCredentials(ev.userId, ev.accessToken);
                }
            },
            onAuthorizedFailed: (ev) => {
                var _a;
                this.log.debug("onAuthorizedFailed()", ev);
                (_a = this._eventClient) === null || _a === void 0 ? void 0 : _a.disconnect();
            },
            onLogout: () => {
                var _a;
                this.log.debug("onLogout()");
                (_a = this._eventClient) === null || _a === void 0 ? void 0 : _a.disconnect();
            },
        };
        this._authClient = new OAuth2Client(args.clientId, args.clientSecret, this.serverUrl, !!args.saveAuthTokenState, authHandlers, this._useStandardOAuth2, this.timers);
        this._apiUserResource = new ApiUserResource(this);
        if (this._authClient.isAuthenticated && !this._authClient.token.isExpired) {
            authHandlers.onAuthorized(this._authClient.token);
        }
    }
    loginTemplate(tokenProviderFn, refreshTokenProviderFn) {
        return this.auth.loginTemplate(tokenProviderFn, refreshTokenProviderFn);
    }
    /**
     * Login at it's purest form.
     *
     * @param username username
     * @param password password
     */
    login(username, password, clientId, organization) {
        if (this._useStandardOAuth2) {
            return this.auth.standardOAuth2Login(username, password);
        }
        else {
            return this.auth.login(clientId, username, password, organization);
        }
    }
    /**
     * Guest Login. Will this ever be used? I don't know.
     *
     * @param username username
     */
    guestLogin(username, clientId, organization) {
        if (this._useStandardOAuth2) {
            return this.auth
                .guestLogin(username)
                .then((guest) => this.auth.standardOAuth2Login(guest.username, guest.password));
        }
        else {
            return this.auth
                .loginGuest(clientId, username, organization, "Guest", "Guestakos")
                .then((guest) => this.auth.login(clientId, guest.username, guest.password, organization));
        }
    }
    /**
     * Logout.
     */
    logout() {
        return this.auth.logout();
    }
    /**
     * Gets the server url.
     * The http host the client targets.
     */
    get serverUrl() {
        if (this._serverUrl) {
            return this._serverUrl;
        }
        return this._host + (this._port !== 0 ? ":" + this._port : "");
    }
    /**
     * Gets the authentication resource instance (OAuth2Client).
     */
    get auth() {
        return this._authClient;
    }
    get user() {
        return this._apiUserResource;
    }
    /**
     * Gets the event client instance (EventClient).
     */
    get events() {
        return this._eventClient;
    }
    /**
     * Gets whether or not the event client is enabled.
     */
    get eventsEnabled() {
        return !!this._eventClient;
    }
    get eventsInitialized() {
        return this._eventClient && this._eventClient.initialized;
    }
    apiResourceFactory(route) {
        if (!route || route.length === 0) {
            throw new Error("Invalid resource route. " +
                "Pass a non empty string instance for the " +
                "resource route into the first argument");
        }
        this.log.info("generateApiResource: ", route);
        return new ApiResource(this, route);
    }
    apiSearchResourceFactory(route, searchType) {
        if (!route || route.length === 0) {
            throw new Error("Invalid resource route. " +
                "Pass a non empty string instance for the " +
                "resource route into the first argument");
        }
        if (!searchType || searchType.length === 0) {
            throw new Error("Invalid resource searchType. " +
                "Pass a non empty string instance for the resource searchType into the " +
                "second argument");
        }
        this.log.info("generateApiSearchResource: ", route);
        return new ApiSearchResource(this, route, searchType);
    }
    delayUsingSetTimeout(ms) {
        return new Promise((resolve) => {
            this.timers.setTimeout(() => resolve(), ms);
        });
    }
    getTimers() {
        return this.timers;
    }
}
//# sourceMappingURL=AuviousCommon.js.map